import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Form, Radio, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FeedbackResult from '@/components/candidates/FeedbackResult';
import { FEEDBACK_RATING_FIELDS } from '@/components/form/feedbackRatingFields';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { useUrlState } from '@/hooks/useUrlState';
import { buildFeedbackFormSchema } from '@/utils/validation/formSchemas/candidateSchemas';

import FormItem from './FormItem';

import type { FormSize } from '@/components/form/SchemaForm/sizing';
import type {
  FieldConfig,
  SchemaSection,
} from '@/components/form/SchemaForm/types';
import type React from 'react';

export const FEEDBACK_FORM_ID = 'add-feedback';

function ratingWithCommentFields(
  ratingName: string,
  commentName: string,
  showKey: string,
  label: string,
  feedbackItems: { name: string; value: string }[],
  commentLabel: string,
): FieldConfig[] {
  return [
    {
      name: showKey,
      type: 'hidden',
    },
    {
      key: `${ratingName}-header`,
      type: 'custom',
      render: ({ form: f, inputSize }) => (
        <Flex justify="space-between" align="flex-start">
          <FormItem name={ratingName} label={label}>
            <Radio.Group
              size={inputSize}
              optionType="button"
              buttonStyle="solid"
              block
            >
              {feedbackItems.map((item) => (
                <Radio.Button key={item.value} value={item.value}>
                  {item.name}
                </Radio.Button>
              ))}
            </Radio.Group>
          </FormItem>
          <Button
            size="small"
            variant="outlined"
            icon={
              f.getFieldValue(showKey) ? <MinusOutlined /> : <PlusOutlined />
            }
            onClick={() => f.setFieldValue(showKey, !f.getFieldValue(showKey))}
          >
            {commentLabel}
          </Button>
        </Flex>
      ),
    },
    {
      name: commentName,
      type: 'textarea',
      hidden: (values) => !values[showKey],
      componentProps: {
        autoSize: { minRows: 3, maxRows: 3 },
        placeholder: commentLabel,
      },
    },
  ];
}

interface TabRadioGroupProps extends React.ComponentProps<typeof Radio.Group> {
  data: { title: string | number; value: string | number }[];
  value?: string | number;
}

function TabRadioButtonFeedback({
  onChange,
  value,
  data,
  className,
  style,
  ...rest
}: TabRadioGroupProps) {
  const { history } = useUrlState();
  const params = new URLSearchParams(history.location.search);
  const recruiter =
    params.get('recruiter') || history.location.pathname.includes('recruiters');
  const client =
    params.get('client') || history.location.pathname.includes('clients');

  return (
    <Radio.Group
      onChange={onChange}
      value={value}
      className={className}
      style={style}
      {...rest}
    >
      {data.map((el) => (
        <Radio
          value={el.value}
          key={el.value}
          disabled={
            !!(
              (recruiter && el.value === 'client') ||
              (client && el.value === 'recruiter')
            )
          }
        >
          {el.title}
        </Radio>
      ))}
    </Radio.Group>
  );
}

export type FeedbackFormValues = Record<string, unknown> & {
  match_with_company?: string;
  match_with_company_comment?: string;
  match_with_role?: string;
  match_with_role_comment?: string;
  match_with_team?: string;
  match_with_team_comment?: string;
  match_with_culture?: string;
  match_with_culture_comment?: string;
  match_with_career?: string;
  match_with_career_comment?: string;
  match_with_tech?: string;
  match_with_tech_comment?: string;
  match_with_interviewers?: string;
  match_with_interviewers_comment?: string;
  comments?: string;
  decision?: string;
  agree?: boolean;
};

interface FeedbackFormProps {
  onComplete: (data: FeedbackFormValues) => void;
  completed: boolean;
  isClient: boolean;
  loading: boolean;
  hideMessage?: boolean;
  data: Partial<FeedbackFormValues> | null;
  showSubmitButton?: boolean;
  size?: FormSize;
}

const FeedbackForm = ({
  onComplete,
  isClient,
  completed,
  data,
  hideMessage = false,
  loading,
  showSubmitButton = true,
  size,
}: FeedbackFormProps) => {
  const [t] = useTranslation();
  const [form] = Form.useForm<FeedbackFormValues>();
  const formSchema = useMemo(() => buildFeedbackFormSchema(t), [t]);

  const onFinish = (values: FeedbackFormValues) => {
    const payload: FeedbackFormValues = { ...values };
    FEEDBACK_RATING_FIELDS.forEach(({ showCommentField }) => {
      delete payload[showCommentField];
    });
    onComplete(payload);
  };

  const feedbackTypes = useMemo<{ title: string; value: string }[]>(
    () => [
      {
        title: t('POSITIVE_DECISION'),
        value: 'positive',
      },
      {
        title: t('NEGATIVE_DECISION'),
        value: 'negative',
      },
    ],
    [t],
  );

  const feedbacks = useMemo<{ name: string; value: string }[]>(
    () => [
      {
        name: t('NEGATIVE'),
        value: 'negative',
      },
      {
        name: t('NEUTRAL'),
        value: 'neutral',
      },
      {
        name: t('POSITIVE'),
        value: 'positive',
      },
    ],
    [t],
  );

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'feedback',
        fields: [
          ...FEEDBACK_RATING_FIELDS.flatMap(
            ({ ratingField, commentField, showCommentField, labelKey }) =>
              ratingWithCommentFields(
                ratingField,
                commentField,
                showCommentField,
                t(labelKey),
                feedbacks,
                t('COMMENT'),
              ),
          ),
          {
            name: 'comments',
            type: 'textarea',
            label: t('GENERAL_COMMENTS'),
            componentProps: {
              autoSize: { minRows: 3, maxRows: 3 },
            },
          },
          {
            name: 'decision',
            type: 'custom',
            label: t('OVERALL_DECISION'),
            render: () => <TabRadioButtonFeedback data={feedbackTypes} />,
          },
          {
            key: 'feedback-info',
            type: 'custom',
            render: () => (
              <Alert
                type="info"
                showIcon={false}
                title={
                  <Flex vertical>
                    <Typography.Text>• {t('FEEDBACK_TEXT_1')}</Typography.Text>
                    <Typography.Text>• {t('FEEDBACK_TEXT_2')}</Typography.Text>
                  </Flex>
                }
              />
            ),
          },
          {
            name: 'agree',
            type: 'checkbox',
            label: t('DOUBLE_CHECKED'),
          },
        ],
      },
    ],
    [t, feedbackTypes, feedbacks],
  );

  const initialValues = useMemo(
    () => ({
      show_company_comment: !!data?.match_with_company_comment,
      show_role_comment: !!data?.match_with_role_comment,
      show_team_comment: !!data?.match_with_team_comment,
      show_culture_comment: !!data?.match_with_culture_comment,
      show_career_comment: !!data?.match_with_career_comment,
      show_tech_comment: !!data?.match_with_tech_comment,
      show_interviewers_comment: !!data?.match_with_interviewers_comment,
    }),
    [data],
  );

  return (
    <Flex vertical align="flex-start">
      {data ? (
        <>
          <FeedbackResult data={data} />
          {completed && !hideMessage && (
            <Alert
              type="success"
              showIcon={false}
              title={t(isClient ? 'RECRUITER_FEEDBACK' : 'CLIENT_FEEDBACK')}
            />
          )}
        </>
      ) : (
        !completed && (
          <SchemaForm
            scrollToFirstError
            size={size}
            name={FEEDBACK_FORM_ID}
            form={form}
            formSchema={formSchema}
            onFinish={onFinish}
            sections={sections}
            initialValues={initialValues}
            footer={
              showSubmitButton || completed
                ? {
                    actions: [
                      ...(showSubmitButton
                        ? [
                            {
                              label: t('SEND'),
                              htmlType: 'submit' as const,
                              type: 'primary' as const,
                              loading,
                            },
                          ]
                        : []),
                    ],
                  }
                : undefined
            }
          >
            {completed && (
              <Alert
                type="success"
                showIcon={false}
                title={t(isClient ? 'RECRUITER_FEEDBACK' : 'CLIENT_FEEDBACK')}
              />
            )}
          </SchemaForm>
        )
      )}
    </Flex>
  );
};

export default FeedbackForm;
